import { pgSelect, pgInsert, pgUpdateWhere, edgeFunctionUrl } from './supabaseClient';
import { loadConfig } from './configService';
import { logEvent } from './debugLog';

/**
 * 🏢 Corporate pack — facility issue reporting (feature: 'facilityReq').
 * Anyone in the building reports an issue where they stand (🛠 in the AR
 * HUD's ⋯ menu); the report is pinned to their map position + nearest POI,
 * lands in the Dashboard's 🛠 Facilities queue, and emails the venue's
 * ops recipients (venue_settings.ops_emails, fallback capture_emails).
 */

export const FACILITY_CATEGORIES = [
  'Cleaning',
  'Repair',
  'Safety hazard',
  'IT / AV',
  'Lighting / electrical',
  'Other',
] as const;

export interface FacilityRequest {
  id: string;
  map_code: string;
  category: string;
  note: string | null;
  reporter: string | null;
  photo_url: string | null;
  poi_id: string | null;
  poi_name: string | null;
  pos_x: number | null;
  pos_y: number | null;
  pos_z: number | null;
  floor: number | null;
  status: 'open' | 'in_progress' | 'resolved';
  created_at: string;
}

export interface NewFacilityRequest {
  category: string;
  note?: string;
  reporter?: string;
  photoUrl?: string;
  poiId?: string;
  poiName?: string;
  pos?: { x: number; y: number; z: number };
  floor?: number;
}

/** Visitor-side: create the report + fire the ops email (best-effort). */
export async function submitFacilityRequest(req: NewFacilityRequest): Promise<void> {
  const mapCode = loadConfig().code;
  await pgInsert('facility_requests', {
    map_code: mapCode,
    category: req.category,
    note: req.note ?? null,
    reporter: req.reporter ?? null,
    photo_url: req.photoUrl ?? null,
    poi_id: req.poiId ?? null,
    poi_name: req.poiName ?? null,
    pos_x: req.pos?.x ?? null,
    pos_y: req.pos?.y ?? null,
    pos_z: req.pos?.z ?? null,
    floor: req.floor ?? null,
  });
  logEvent('session', `facility report submitted: ${req.category}${req.poiName ? ` near ${req.poiName}` : ''}`);
  // Email is best-effort — the queue row is the source of truth.
  fetch(edgeFunctionUrl('notify-facility'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mapCode,
      category: req.category,
      note: req.note,
      poiName: req.poiName,
      reporter: req.reporter,
      photoUrl: req.photoUrl,
    }),
  }).catch(() => undefined);
}

/** Dashboard: the venue's queue, newest first (staff-only via RLS). */
export async function listFacilityRequests(mapCode: string): Promise<FacilityRequest[]> {
  return pgSelect<FacilityRequest>(
    'facility_requests',
    `select=*&map_code=eq.${encodeURIComponent(mapCode)}&order=created_at.desc&limit=100`,
  );
}

/** Dashboard: advance a request's status. */
export async function setFacilityStatus(id: string, status: FacilityRequest['status']): Promise<void> {
  await pgUpdateWhere('facility_requests', `id=eq.${encodeURIComponent(id)}`, {
    status,
    resolved_at: status === 'resolved' ? new Date().toISOString() : null,
  });
  logEvent('admin', `facility request ${id.slice(0, 8)} → ${status}`);
}
